import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from "../components/Layout";
import { fmtDate } from "../lib/format";
import api from "../lib/api";
import { useRefresh } from "../contexts/RefreshContext";
import { toast } from "sonner";
import { Truck, Package, Stack, Trash, FloppyDisk, ArrowRight, CloudArrowUp, FileText, MagnifyingGlass, ListChecks, Envelope, X, Printer, PencilSimple, MapPin, Paperclip, LinkSimple, CheckCircle, Warning, CircleNotch } from "@phosphor-icons/react";

const fmt = (n) => (Number(n) || 0).toLocaleString("en-US");
const money = (n) => "$" + (Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const wt = (n) => (Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 3, maximumFractionDigits: 3 });
// currency label + currency-aware money (currency comes from the PO)
const curLabel = (c) => ({ USD: "US $", INR: "\u20B9", EUR: "\u20AC", GBP: "\u00A3" }[c] || c || "");
const moneyC = (n, ccy) => `${curLabel(ccy) || "$"} ${(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const PLANTS = ["P1", "P2", "P4", "P7", "P9"];
const cartKey = (l) => `${l.po}-${l.release}-${l.icode}`;

// days from today to an ISO date (negative = overdue); null if no date
const daysTo = (iso) => { if (!iso) return null; return Math.round((new Date(iso) - new Date()) / 86400000); };

// Human-readable pack label, e.g. "Pallet · 4/bx · 1 bx/plt" or "Carton · 12/bx"
const packLabel = (l) => {
  if (l.pack_basis === "pallet") {
    const parts = ["Pallet"];
    if (l.units_per_box) parts.push(`${l.units_per_box}/bx`);
    if (l.boxes_per_pallet) parts.push(`${l.boxes_per_pallet} bx/plt`);
    return parts.join(" · ");
  }
  if (l.pack_basis === "carton") {
    return l.units_per_box ? `Carton · ${l.units_per_box}/bx` : "Carton";
  }
  return l.pack || "Unit";
};
const passFilter = (iso, f) => {
  const d = daysTo(iso);
  if (f === "all") return true;
  if (d === null) return f === "all";
  if (f === "overdue") return d < 0;
  if (f === "30") return d <= 30;
  if (f === "90") return d <= 90;
  return true;
};

export default function DispatchPlan() {
  const [customers, setCustomers] = useState([]);
  const [pcode, setPcode] = useState("");
  const { tick } = useRefresh();
  const [mode, setMode] = useState("po");            // 'po' | 'item'
  const [filter, setFilter] = useState("90");
  const [search, setSearch] = useState("");

  const [pos, setPos] = useState([]);
  const [items, setItems] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [tpInfo, setTpInfo] = useState(null);        // transfer-party routing for the selected customer
  const [selKey, setSelKey] = useState("");          // selected PO number or icode
  const [lines, setLines] = useState([]);
  const [qty, setQty] = useState({});                // by line cartKey within current selection
  const [loadingList, setLoadingList] = useState(false);
  const [loadingLines, setLoadingLines] = useState(false);

  const [cart, setCart] = useState([]);
  const [consolidate, setConsolidate] = useState([]);
  // Same-item consolidation is ON by default (combine an item's qty across PO lines onto shared
  // pallets). userUnchecked tracks items the user explicitly opted out of; pendingDraftConsolidate
  // holds a loaded draft's saved choice so we honour it instead of re-defaulting everything on.
  const userUnchecked = useRef(new Set());
  const pendingDraftConsolidate = useRef(null);
  const [preview, setPreview] = useState(null);
  const [plans, setPlans] = useState([]);
  const [saving, setSaving] = useState(false);
  const [posting, setPosting] = useState(null);
  const [editingId, setEditingId] = useState(null);  // draft plan being edited (PUT vs POST)
  const [transferPrice, setTransferPrice] = useState(false); // use dbo.Partyi rate instead of PO price
  const [tpOn, setTpOn] = useState(true);   // per-dispatch transfer-party override (default ON from master TP_CODE)
  const [hasApprovedTp, setHasApprovedTp] = useState(false);  // customer has approved transfer prices (no TP party)
  const [useTpStandalone, setUseTpStandalone] = useState(false); // standalone transfer-pricing toggle (no TP party)
  const pendingTpOn = useRef(null);          // holds a loaded draft's saved TP choice across the address reload
  const pendingManualTp = useRef(null);      // holds a loaded draft's MANUAL TP (customer has no master TP_CODE)
  const [tpOptions, setTpOptions] = useState([]);   // available transfer parties (e.g. AVM) for manual routing
  const [manualTp, setManualTp] = useState(null);   // manually chosen TP for customers with no master TP_CODE (e.g. MPY)
  const [summary, setSummary] = useState(null);       // dispatch-completion email preview
  const [openingSummary, setOpeningSummary] = useState(null); // plan id whose email preview is loading
  const [summaryInv, setSummaryInv] = useState({ invoice_no: "", invoice_date: "" });
  const [masterItem, setMasterItem] = useState(null); // item needing master details before it can be added
  const [dcSug, setDcSug] = useState(null);           // captured-DC → ship-to suggestion for the selected PO (Walmart etc.)

  const [hdr, setHdr] = useState({
    plant: "", consignee_code: "", dispatch_date: new Date().toISOString().slice(0, 10),
    ship_type: "", currency: "USD", first_car_no: "1", po_nos: "", dstatus: "Ready",
  });
  const setH = (k, v) => setHdr((s) => ({ ...s, [k]: v }));
  const [showReq, setShowReq] = useState(false);   // surfaces red hints on required header fields
  const [planTab, setPlanTab] = useState("staged"); // 'staged' (drafts) | 'posted'
  const [planSearch, setPlanSearch] = useState(""); // search staged/posted plans by ID (or customer/voc)

  useEffect(() => { api.get("/ms/customers").then((r) => setCustomers(r.data || [])).catch(() => {}); loadPlans(); api.get("/dispatch/transfer-party-options").then((r) => setTpOptions(r.data || [])).catch(() => {}); }, []);
  const loadPlans = () => api.get("/dispatch/plan").then((r) => setPlans(r.data || [])).catch(() => {});

  // When a single PO is selected (By-PO mode), look up its captured DC (from intake) and suggest
  // the matching ship-to consignee. Read-only: the user clicks "Apply" to set it.
  useEffect(() => {
    setDcSug(null);
    if (mode !== "po" || !pcode || !selKey || editingId) return;
    const selPo = pos.find((p) => p.po === selKey);
    const nm = customers.find((c) => c.ms_pcode === pcode)?.name || "";
    const params = new URLSearchParams({ pcode, po: String(selKey), name: nm });
    if (selPo?.custpo) params.set("custpo", String(selPo.custpo));
    api.get(`/dispatch/plan/dc-suggestion?${params.toString()}`)
      .then((r) => { if (r.data?.found) setDcSug(r.data); })
      .catch(() => {});
  }, [selKey, mode, pcode, pos, editingId, customers]);

  // load PO / Item summaries when customer or mode changes
  useEffect(() => {
    setSelKey(""); setLines([]); setQty({});
    if (!pcode) { setPos([]); setItems([]); return; }
    setLoadingList(true);
    const url = mode === "po" ? `/dispatch/plan/open-pos?pcode=${pcode}` : `/dispatch/plan/open-items?pcode=${pcode}`;
    api.get(url).then((r) => { mode === "po" ? setPos(r.data || []) : setItems(r.data || []); })
      .catch(() => toast.error("Failed to load list")).finally(() => setLoadingList(false));
  }, [pcode, mode, tick]);

  // load the customer's ship-to / consignee addresses (from MS SQL paddress).
  // If the customer routes via a transfer party, the address list is the transfer
  // party's and the consignee is auto-selected to it.
  useEffect(() => {
    if (!pcode) { setAddresses([]); setTpInfo(null); setHasApprovedTp(false); setUseTpStandalone(false); setManualTp(null); return; }
    const nm = customers.find((c) => c.ms_pcode === pcode)?.name || "";
    setManualTp(null);
    api.get(`/dispatch/plan/addresses?pcode=${encodeURIComponent(pcode)}&name=${encodeURIComponent(nm)}`)
      .then((r) => {
        const d = r.data || {};
        setAddresses(d.addresses || []);
        const hasTp = !!d.transfer_party;
        setTpInfo(hasTp ? { tp_code: d.tp_code, tp_name: d.tp_name, customer_name: d.customer_name || nm } : null);
        // Default the override ON when a customer has a transfer party; honour a loaded
        // draft's saved choice (pendingTpOn) if we're editing.
        if (hasTp) setTpOn(pendingTpOn.current !== null ? pendingTpOn.current : true);
        // Restore a MANUALLY-routed transfer party from a loaded draft when the customer has no
        // master TP_CODE (e.g. MPY routed via AVM). Only applies when there's no auto TP.
        if (!hasTp && pendingManualTp.current) {
          setManualTp(pendingManualTp.current);
          setTpOn(pendingTpOn.current !== null ? pendingTpOn.current : true);
        }
        pendingTpOn.current = null;
        pendingManualTp.current = null;
        // Standalone transfer pricing: available for customers that have transfer rates in
        // the ERP master (dbo.Partyi) but NO transfer party (e.g. MacLean Senior Industries).
        setHasApprovedTp(!!d.has_transfer_prices);
      })
      .catch(() => { setAddresses([]); setTpInfo(null); setHasApprovedTp(false); });
    setUseTpStandalone(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pcode, customers]);

  // load release lines for the selected PO / item
  const reloadLines = async () => {
    if (!pcode || !selKey) { setLines([]); return []; }
    const q = mode === "po" ? `po=${encodeURIComponent(selKey)}` : `icode=${encodeURIComponent(selKey)}`;
    const tp = transferPrice ? "&transfer_price=1" : "";
    const r = await api.get(`/dispatch/plan/open-lines?pcode=${pcode}&${q}${tp}`);
    setLines(r.data || []);
    return r.data || [];
  };
  useEffect(() => {
    if (!pcode || !selKey) { setLines([]); return; }
    setLoadingLines(true);
    reloadLines().then(() => setQty({}))
      .catch(() => toast.error("Failed to load lines")).finally(() => setLoadingLines(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selKey, pcode, mode, transferPrice]);

  // Transfer-party routing ALWAYS uses transfer-party rates → keep transferPrice in lock-step
  // with tpOn. For customers WITHOUT a transfer party but WITH approved transfer prices, a
  // standalone "Use transfer pricing" toggle (useTpStandalone) drives pricing on its own.
  useEffect(() => {
    if (tpInfo || manualTp) setTransferPrice(tpOn);
    else setTransferPrice(hasApprovedTp && useTpStandalone);
  }, [tpInfo, manualTp, tpOn, hasApprovedTp, useTpStandalone]);

  const setQ = (l, v) => {
    const n = Math.max(0, Math.min(Number(l.bal_qty) || 0, parseFloat(v || "0") || 0));
    setQty((s) => ({ ...s, [cartKey(l)]: n }));
  };

  const addToCart = (srcLines = lines) => {
    const add = srcLines.filter((l) => (qty[cartKey(l)] || 0) > 0)
      .map((l) => ({ ...l, disp_qty: qty[cartKey(l)] }));
    if (!add.length) return toast.error("Enter a dispatch qty first");
    // Transfer-price mode: a line with no approved transfer price is blocked (never uses PO price).
    const blockedTp = add.find((l) => l.needs_transfer_price);
    if (blockedTp) {
      const msg = blockedTp.tp_reason === "expired"
        ? `Transfer price for ${blockedTp.icode} expired on ${blockedTp.tp_valid_until}. Renew it in Settings › Transfer Pricing first.`
        : `No approved transfer price for ${blockedTp.icode}. Set it in Settings › Transfer Pricing first.`;
      return toast.error(msg);
    }
    // Block items whose MS SQL master lacks packing/weights — capture those details first.
    const blocked = add.find((l) => l.needs_master);
    if (blocked) { setMasterItem(blocked); return; }
    setCart((c) => [...c.filter((x) => !add.find((a) => cartKey(a) === cartKey(x))), ...add]);
    setQty({});
    toast.success(`Added ${add.length} line(s) to the dispatch plan`);
  };

  const saveMaster = async (form) => {
    const ic = masterItem.icode;
    await api.post(`/dispatch/item-master/${encodeURIComponent(ic)}`, form);
    toast.success(`Item ${ic} master updated`);
    setMasterItem(null);
    const fresh = await reloadLines();                 // pull the now-complete pack/weights
    if (mode === "po") { api.get(`/dispatch/plan/open-pos?pcode=${pcode}`).then((r) => setPos(r.data || [])); }
    else { api.get(`/dispatch/plan/open-items?pcode=${pcode}`).then((r) => setItems(r.data || [])); }
    addToCart(fresh);                                  // auto-add now that details exist
  };
  const removeCart = (l) => setCart((c) => c.filter((x) => cartKey(x) !== cartKey(l)));
  const updateCartQty = (l, v) => {
    const n = Math.max(0, Math.min(Number(v) || 0, Number(l.bal_qty) || Infinity));
    setCart((c) => c.map((x) => (cartKey(x) === cartKey(l) ? { ...x, disp_qty: n } : x)));
  };

  // filtered + due-date-sorted lists (earliest due / overdue first)
  const filteredPos = useMemo(() => pos.filter((p) => passFilter(p.earliest_due, filter) &&
    (!search || `${p.po} ${p.custpo}`.toLowerCase().includes(search.toLowerCase())))
    .sort((a, b) => (a.earliest_due || "9999-12-31").localeCompare(b.earliest_due || "9999-12-31")), [pos, filter, search]);
  const filteredItems = useMemo(() => items.filter((it) => passFilter(it.earliest_due, filter) &&
    (!search || it.icode.toLowerCase().includes(search.toLowerCase())))
    .sort((a, b) => (a.earliest_due || "9999-12-31").localeCompare(b.earliest_due || "9999-12-31")), [items, filter, search]);

  // same-item consolidation candidates in the cart
  const groups = useMemo(() => {
    const m = {};
    cart.forEach((c) => { (m[c.icode] = m[c.icode] || []).push(c); });
    return Object.entries(m).filter(([, ls]) => ls.length > 1).map(([ic, ls]) => ({ icode: ic, count: ls.length }));
  }, [cart]);
  const toggleConsolidate = (ic) => setConsolidate((s) => {
    if (s.includes(ic)) { userUnchecked.current.add(ic); return s.filter((x) => x !== ic); }
    userUnchecked.current.delete(ic); return [...s, ic];
  });

  // Default same-item groups to consolidated (combine qty across PO lines) unless the user
  // opted out, or a loaded draft explicitly had them off.
  useEffect(() => {
    const groupIcodes = groups.map((g) => g.icode);
    if (pendingDraftConsolidate.current !== null) {
      const saved = new Set(pendingDraftConsolidate.current);
      groupIcodes.forEach((ic) => { if (!saved.has(ic)) userUnchecked.current.add(ic); });
      pendingDraftConsolidate.current = null;
    }
    setConsolidate((prev) => {
      const next = new Set(prev.filter((ic) => groupIcodes.includes(ic)));
      groupIcodes.forEach((ic) => { if (!userUnchecked.current.has(ic)) next.add(ic); });
      const arr = Array.from(next);
      return (arr.length === prev.length && arr.every((x) => prev.includes(x))) ? prev : arr;
    });
  }, [groups]);

  const selAddr = useMemo(() => addresses.find((a) => a.padd_code === hdr.consignee_code) || null, [addresses, hdr.consignee_code]);
  const selPo = useMemo(() => pos.find((p) => p.po === selKey) || null, [pos, selKey]);
  const custCurrency = useMemo(() => cart[0]?.currency || lines[0]?.currency || (mode === "po" ? pos[0]?.currency : items[0]?.currency) || "USD", [cart, lines, pos, items, mode]);
  const effTp = tpInfo || manualTp;   // effective transfer party (master TP_CODE, or a manual pick e.g. AVM for MPY)
  const isManualTp = !tpInfo && !!manualTp;

  const buildPayload = () => ({
    ...hdr, pcode, customer_name: customers.find((c) => c.ms_pcode === pcode)?.name || "",
    consignee_name: selAddr?.consignee || "",
    currency: cart[0]?.currency || hdr.currency,   // currency comes from the PO, not a fixed default
    consolidate_icodes: consolidate, pack: "Pallet", transfer_price: transferPrice,
    transfer_party: !!(tpInfo || manualTp) && tpOn,
    tp_code: ((tpInfo || manualTp) && tpOn) ? (tpInfo || manualTp).tp_code : null,
    tp_name: ((tpInfo || manualTp) && tpOn) ? (tpInfo || manualTp).tp_name : null,
    lines: cart.map((r) => ({
      po: r.po, custpo: r.custpo, cust_po2: r.cust_po2, po_date: r.po_date, release: r.release, line_idx: r.line_idx,
      icode: r.icode, iname: r.iname, rev: r.rev, unit: r.unit, deliv_date: r.deliv_date,
      po_qty: r.po_qty, bal_qty: r.bal_qty, rate: r.rate, disp_qty: r.disp_qty, currency: r.currency,
      po_rate: r.po_rate, transfer_rate: r.transfer_rate,
      pallet_qty: r.pallet_qty, units_per_box: r.units_per_box, boxes_per_pallet: r.boxes_per_pallet,
      pack_basis: r.pack_basis, pack: r.pack,
      net_wt_pcs: r.net_wt_pcs, gross_wt_pcs: r.gross_wt_pcs, lsize: r.lsize, wsize: r.wsize, hsize: r.hsize,
    })),
  });

  // live preview from cart
  useEffect(() => {
    if (!cart.length) { setPreview(null); return; }
    const t = setTimeout(() => { api.post("/dispatch/plan/preview", buildPayload()).then((r) => setPreview(r.data)).catch(() => {}); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(cart.map((c) => [cartKey(c), c.disp_qty])), JSON.stringify(consolidate), hdr.first_car_no]);

  const save = async () => {
    const miss = [];
    if (!pcode) miss.push("Customer");
    if (!hdr.plant) miss.push("Plant");
    if (!hdr.consignee_code) miss.push("Consignee / Ship-to");
    if (!hdr.ship_type) miss.push("Ship Type");
    if (miss.length) { setShowReq(true); return toast.error(`Required: ${miss.join(", ")}`); }
    if (!cart.length) return toast.error("Add lines to the plan first");
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/dispatch/plan/${editingId}`, buildPayload());
        toast.success("Dispatch plan updated");
      } else {
        await api.post("/dispatch/plan", buildPayload());
        toast.success("Dispatch plan saved (draft)");
      }
      setCart([]); setConsolidate([]); setEditingId(null); setShowReq(false); loadPlans();
    }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };
  const editPlan = async (p) => {
    try {
      const d = (await api.get(`/dispatch/plan/${p.id}`)).data;
      pendingTpOn.current = !!d.transfer_party;   // honoured by the address effect once pcode loads
      pendingManualTp.current = (d.transfer_party && d.tp_code)
        ? { tp_code: d.tp_code, tp_name: d.tp_name } : null;   // restored only if customer has no master TP
      setPcode(d.pcode || "");
      setHdr((s) => ({
        ...s,
        plant: d.plant || s.plant,
        consignee_code: d.consignee_code || "",
        dispatch_date: (d.dispatch_date || "").slice(0, 10) || s.dispatch_date,
        ship_type: d.ship_type || s.ship_type,
        currency: d.currency || s.currency,
        first_car_no: String(d.first_car_no || "1"),
        po_nos: d.po_nos || "",
        dstatus: d.dstatus || "Ready",
      }));
      setCart((d.lines || []).map((l) => ({ ...l, disp_qty: Number(l.disp_qty) || 0 })));
      setSelKey("");
      userUnchecked.current = new Set();
      pendingDraftConsolidate.current = d.consolidate_icodes || [];
      setConsolidate(d.consolidate_icodes || []);
      setEditingId(p.id);
      toast.success(d.status === "posted" ? "Posted plan loaded — edit lines, Update, then Re-post to MS SQL" : "Draft loaded — edit qty/lines and click Update");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not load plan"); }
  };
  const cancelEdit = () => { setEditingId(null); setCart([]); setConsolidate([]); userUnchecked.current = new Set(); pendingDraftConsolidate.current = null; toast("Edit cancelled"); };

  // Deep-link edit: /dispatch-plan?edit=<planId> loads that draft into the editor
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const eid = searchParams.get("edit");
    if (eid) { editPlan({ id: eid }); setSearchParams({}, { replace: true }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const postPlan = async (p) => {
    setPosting(p.id);
    try {
      const d = (await api.post(`/dispatch/plan/${p.id}/post?confirm=false`)).data;
      if (!window.confirm(`Post dispatch to MS SQL portal?\n\n${p.customer_name || p.pcode} · Plant ${p.plant}\n${d.line_count} line(s) · ${fmt(d.tot_qty)} units · ${d.tot_cartons} containers\nNet ${wt(d.tot_net_wt)} / Gross ${wt(d.tot_gross_wt)} · ${moneyC(d.tot_amount, p.currency)}\n\nCreates a new Voc_No. Invoice No stays blank (legacy).`)) { setPosting(null); return; }
      const res = (await api.post(`/dispatch/plan/${p.id}/post?confirm=true`)).data;
      toast.success(`Posted · Voc_No ${res.voc_no}`); loadPlans();
    } catch (e) { toast.error(e.response?.data?.detail || "Post failed"); } finally { setPosting(null); }
  };
  const repostPlan = async (p) => {
    setPosting(p.id);
    try {
      const d = (await api.post(`/dispatch/plan/${p.id}/repost?confirm=false`)).data;
      if (!window.confirm(`Re-post dispatch to MS SQL portal?\n\nThis REWRITES the existing Voc# ${d.voc_no} — the old lines are removed and replaced with the edited plan.\n\n${p.customer_name || p.pcode} · Plant ${p.plant}\n${d.line_count} line(s) · ${fmt(d.tot_qty)} units · ${d.tot_cartons} containers\nNet ${wt(d.tot_net_wt)} / Gross ${wt(d.tot_gross_wt)} · ${moneyC(d.tot_amount, p.currency)}\n\nRefused if an invoice already exists for this dispatch.`)) { setPosting(null); return; }
      const res = (await api.post(`/dispatch/plan/${p.id}/repost?confirm=true`)).data;
      toast.success(`Re-posted · Voc_No ${res.voc_no}`); loadPlans();
    } catch (e) { toast.error(e.response?.data?.detail || "Re-post failed"); } finally { setPosting(null); }
  };
  const delPlan = async (p) => {
    const msg = p.status === "posted"
      ? `Delete dispatch Voc# ${p.voc_no} from the system?\n\n${p.customer_name || p.pcode} · ${fmt(p.tot_qty)} EA · ${moneyC(p.tot_amount, p.currency)}\n\nThis removes the dispatch from the system and cannot be undone.`
      : "Delete this draft plan?";
    if (!window.confirm(msg)) return;
    try { await api.delete(`/dispatch/plan/${p.id}`); toast.success(p.status === "posted" ? "Dispatch deleted from system" : "Draft deleted"); loadPlans(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const openSummary = async (p) => {
    if (openingSummary) return;               // ignore rapid double-clicks while loading
    setOpeningSummary(p.id);
    try {
      const d = (await api.get(`/dispatch/plan/${p.id}/summary`)).data;
      setSummary({ ...d, _planId: p.id });
      setSummaryInv({ invoice_no: d.invoice_no || "", invoice_date: d.invoice_date || "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not load summary"); }
    finally { setOpeningSummary(null); }
  };

  const th = { textAlign: "left", fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4, color: "#6B7280", padding: "6px 9px", borderBottom: "1px solid var(--jde-border)", fontWeight: 700, whiteSpace: "nowrap" };
  const td = { padding: "8px 9px", fontSize: 13, borderBottom: "1px solid #EEF1F4" };
  const numIn = { width: 90, textAlign: "right", padding: "6px 8px", border: "2px solid #2A5C9B", borderRadius: 4, fontFamily: "var(--jde-font-mono)", fontSize: 14, fontWeight: 600 };
  const actBtn = { padding: "5px 10px", height: 30, border: "1px solid var(--jde-border)", borderRadius: 5, background: "white", fontSize: 11, fontWeight: 700, cursor: "pointer", display: "inline-flex", gap: 5, alignItems: "center", justifyContent: "center", whiteSpace: "nowrap", lineHeight: 1 };
  const chip = (a) => ({ padding: "4px 11px", borderRadius: 999, border: "1px solid " + (a ? "var(--omh-primary,#2A5C9B)" : "var(--jde-border)"), background: a ? "#EAF1FB" : "white", color: a ? "var(--omh-primary,#2A5C9B)" : "#6B7280", fontSize: 12.5, fontWeight: 600, cursor: "pointer" });
  const selName = customers.find((c) => c.ms_pcode === pcode)?.name;
  const visiblePlans = plans.filter((p) => {
    const tabOk = planTab === "posted" ? p.status === "posted" : p.status !== "posted";
    if (!tabOk) return false;
    const q = planSearch.trim().toLowerCase();
    if (!q) return true;
    const dpid = ("dp-" + (p.id || "").slice(-6)).toLowerCase();
    return dpid.includes(q)
      || (p.id || "").toLowerCase().includes(q)
      || (p.customer_name || "").toLowerCase().includes(q)
      || (p.pcode || "").toLowerCase().includes(q)
      || String(p.voc_no || "").toLowerCase().includes(q);
  });

  return (
    <Layout breadcrumb={["Dispatch Planning"]}>
      <div className="jde-page">
        {/* toolbar */}
        <style>{`
          .dp-toolbar { display: flex; flex-wrap: wrap; align-items: flex-end; gap: 12px 14px; }
          .dp-field { display: flex; flex-direction: column; gap: 4px; }
          .dp-field > .dp-label { font-size: 10.5px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; color: var(--omh-text-muted); }
          .dp-field > .dp-label .req { color: var(--omh-alert); margin-left: 2px; }
          .dp-toolbar .jde-select, .dp-toolbar .jde-input { height: 36px; font-size: 12.5px; }
          .dp-toolbar .dp-invalid { border-color: var(--omh-alert) !important; box-shadow: 0 0 0 3px rgba(225,29,72,0.10); }
          .dp-search { display: flex; align-items: center; gap: 6px; background: #fff; border: 1px solid var(--omh-border-strong);
            border-radius: var(--omh-radius-sm); height: 36px; padding: 0 10px; transition: border-color .15s, box-shadow .15s; }
          .dp-search:focus-within { border-color: var(--omh-primary); box-shadow: 0 0 0 3px rgba(29,78,216,0.12); }
          .dp-search input { border: 0; outline: none; background: transparent; font-size: 12.5px; width: 170px; color: var(--omh-text); font-family: inherit; }
          .dp-seg { display: inline-flex; border: 1px solid var(--omh-border-strong); border-radius: var(--omh-radius-sm); overflow: hidden; height: 36px; }
          .dp-seg button { display: inline-flex; align-items: center; gap: 6px; padding: 0 15px; border: 0; background: #fff;
            color: var(--omh-text); font-weight: 700; font-size: 12.5px; cursor: pointer; transition: background .15s, color .15s; }
          .dp-seg button + button { border-left: 1px solid var(--omh-border-strong); }
          .dp-seg button.active { background: var(--omh-primary); color: #fff; }
          .dp-seg.dp-seg-sm { height: 28px; }
          .dp-seg.dp-seg-sm button { height: 26px; padding: 0 13px; font-size: 11.5px; }
          /* Builder layout: 3 columns on desktop, 2 on tablet, stacked on phone */
          .dp-3col { display: grid; grid-template-columns: 280px 1fr 320px; gap: 12px; margin-top: 12px; align-items: start; }
          .dp-cart { position: sticky; top: 12px; display: flex; flex-direction: column; gap: 12px; }
          @media (max-width: 1024px) {
            .dp-3col { grid-template-columns: 240px 1fr; }
            .dp-cart { grid-column: 1 / -1; position: static; }
          }
          @media (max-width: 720px) {
            .dp-3col { grid-template-columns: 1fr; }
            .dp-cart { position: static; }
          }
        `}</style>
        <div className="jde-toolbar dp-toolbar" data-testid="dp-toolbar">
          {/* lookups grouped together on the left */}
          <div className="dp-field">
            <div className="dp-label">Customer<span className="req">*</span></div>
            <select className={`jde-select${showReq && !pcode ? " dp-invalid" : ""}`} value={pcode} onChange={(e) => { setPcode(e.target.value); setH("consignee_code", ""); }} data-testid="dp-customer" style={{ minWidth: 220 }}>
              <option value="">Select customer…</option>
              {customers.filter((c) => c.ms_pcode).map((c) => <option key={c.ms_pcode} value={c.ms_pcode}>{c.name} ({c.ms_pcode})</option>)}
            </select>
          </div>
          <div className="dp-field">
            <div className="dp-label">Search {mode === "po" ? "PO" : "item"}</div>
            <div className="dp-search">
              <MagnifyingGlass size={14} weight="bold" style={{ color: "var(--omh-text-muted)", flex: "0 0 auto" }} />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={mode === "po" ? "Search PO…" : "Search item…"} data-testid="dp-search" />
            </div>
          </div>

          <div className="dp-field">
            <div className="dp-label">Dispatch by</div>
            <div className="dp-seg">
              <button type="button" data-testid="dp-mode-po" className={mode === "po" ? "active" : ""} onClick={() => setMode("po")}><FileText size={15} weight="bold" /> PO</button>
              <button type="button" data-testid="dp-mode-item" className={mode === "item" ? "active" : ""} onClick={() => setMode("item")}><Package size={15} weight="bold" /> Item</button>
            </div>
          </div>
          <div className="dp-field">
            <div className="dp-label">Plant<span className="req">*</span></div>
            <select className={`jde-select${showReq && !hdr.plant ? " dp-invalid" : ""}`} value={hdr.plant} onChange={(e) => setH("plant", e.target.value)} data-testid="dp-plant" style={{ minWidth: 120 }}>
              <option value="">Select plant…</option>
              {PLANTS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div className="dp-field">
            <div className="dp-label">Consignee / Ship-to<span className="req">*</span></div>
            {addresses.length > 0 ? (
              <select className={`jde-select${showReq && !hdr.consignee_code ? " dp-invalid" : ""}`} value={hdr.consignee_code} onChange={(e) => setH("consignee_code", e.target.value)} data-testid="dp-consignee" style={{ minWidth: 240 }}>
                <option value="">Select address…</option>
                {addresses.map((a) => (<option key={a.padd_code} value={a.padd_code}>{a.padd_code} — {a.consignee}{a.city ? ` · ${a.city}` : ""}</option>))}
              </select>
            ) : (
              <input className={`jde-input${showReq && !hdr.consignee_code ? " dp-invalid" : ""}`} value={hdr.consignee_code} onChange={(e) => setH("consignee_code", e.target.value)} data-testid="dp-consignee" placeholder={pcode ? "no saved address — type code" : "e.g. WMRT1"} style={{ minWidth: 160 }} />
            )}
            {selAddr?.address && <div className="muted" data-testid="dp-consignee-preview" style={{ fontSize: 10, marginTop: 3, maxWidth: 240, lineHeight: 1.3 }}>{selAddr.address}{selAddr.gstin ? ` · GSTIN ${selAddr.gstin}` : ""}</div>}
            {dcSug && dcSug.suggested_consignee_code && dcSug.suggested_consignee_code !== hdr.consignee_code && (
              <div data-testid="dp-dc-suggestion" style={{ marginTop: 6, display: "flex", alignItems: "flex-start", gap: 8, maxWidth: 300, padding: "7px 9px", borderRadius: 7, border: "1px solid #fed7aa", background: "#fff7ed" }}>
                <MapPin size={14} weight="fill" color="#B45309" style={{ marginTop: 1, flexShrink: 0 }} />
                <div style={{ fontSize: 11, lineHeight: 1.35 }}>
                  <div style={{ color: "#9a3412" }}>DC <b>{dcSug.dc_number || "?"}</b> detected on PO<span style={{ color: "#c2703a" }}> ({dcSug.ship_to_address})</span></div>
                  <div style={{ fontWeight: 700, color: "#7c2d12", margin: "1px 0 4px" }}>{dcSug.suggested_consignee_code} · {dcSug.suggested_consignee}</div>
                  <button type="button" data-testid="dp-dc-apply" onClick={() => setH("consignee_code", dcSug.suggested_consignee_code)}
                    style={{ padding: "3px 12px", fontSize: 11, fontWeight: 800, color: "#fff", background: "#B45309", border: "none", borderRadius: 6, cursor: "pointer" }}>
                    Apply ship-to
                  </button>
                </div>
              </div>
            )}
            {dcSug && !dcSug.suggested_consignee_code && (
              <div data-testid="dp-dc-nomatch" className="muted" style={{ marginTop: 6, fontSize: 10.5, maxWidth: 300, lineHeight: 1.35 }}>
                DC on PO: <b>{dcSug.ship_to_address}</b>{dcSug.dc_number ? ` (#${dcSug.dc_number})` : ""} — no matching ship-to, select manually.
              </div>
            )}
          </div>
          <div className="dp-field">
            <div className="dp-label">Ship type<span className="req">*</span></div>
            <select className={`jde-select${showReq && !hdr.ship_type ? " dp-invalid" : ""}`} value={hdr.ship_type} onChange={(e) => setH("ship_type", e.target.value)} data-testid="dp-ship-type" style={{ minWidth: 110 }}>
              <option value="">Select…</option>
              {["FCL", "LCL", "AIR"].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="dp-field">
            <div className="dp-label">Dispatch date</div>
            <input type="date" className="jde-input" value={hdr.dispatch_date} onChange={(e) => setH("dispatch_date", e.target.value)} data-testid="dp-date" />
          </div>
        </div>

        {pcode && <div data-testid="dp-tp-banner" style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", padding: "8px 12px", borderRadius: 8, border: "1px solid " + (((effTp && tpOn) || (!effTp && useTpStandalone)) ? "#0F766E" : "var(--jde-border)"), background: ((effTp && tpOn) || (!effTp && useTpStandalone)) ? "#F0FDFA" : "#F9FAFB" }}>
          <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.4, textTransform: "uppercase", color: "#6B7280" }}>{effTp ? "Transfer Party" : (hasApprovedTp ? "Transfer Pricing" : "Transfer Party")}</span>
          {effTp ? (
            <>
              <span style={{ fontSize: 12.5, color: tpOn ? "#0F766E" : "#9CA3AF", fontWeight: 700, textDecoration: tpOn ? "none" : "line-through" }} data-testid="dp-tp-name">
                {effTp.tp_name || effTp.tp_code} <span className="muted" style={{ fontWeight: 500 }}>({effTp.tp_code})</span>
              </span>
              {isManualTp && <span className="jde-badge" data-testid="dp-tp-manual-tag" style={{ fontSize: 8.5, background: "#FEF3C7", color: "#92400E", borderColor: "#FCD34D" }} title="Routed manually — this customer has no transfer party in the ERP master">manual</span>}
              <span className="muted" style={{ fontSize: 12, fontWeight: 600 }}>· ordered by {effTp.customer_name || selName}</span>
              <span style={{ flex: 1 }} />
              <span className="muted" style={{ fontSize: 11, fontWeight: 700 }} title="Routing via the transfer party also applies transfer-party rates">Route via transfer party?</span>
              <div style={{ display: "flex", border: "1px solid var(--jde-border)", borderRadius: 999, overflow: "hidden" }}>
                <span data-testid="dp-tp-override-no" onClick={() => setTpOn(false)} style={{ padding: "4px 14px", fontSize: 11, fontWeight: 700, cursor: "pointer", background: !tpOn ? "#6B7280" : "white", color: !tpOn ? "white" : "#6B7280" }}>No</span>
                <span data-testid="dp-tp-override-yes" onClick={() => setTpOn(true)} style={{ padding: "4px 14px", fontSize: 11, fontWeight: 700, cursor: "pointer", background: tpOn ? "#0F766E" : "white", color: tpOn ? "white" : "#6B7280" }}>Yes</span>
              </div>
              {isManualTp && <span data-testid="dp-tp-manual-clear" onClick={() => setManualTp(null)} title="Remove transfer-party routing" style={{ cursor: "pointer", color: "#9CA3AF", fontWeight: 800, fontSize: 14, lineHeight: 1 }}>×</span>}
              {tpOn
                ? <span style={{ fontSize: 11, width: "100%", color: "#0F766E", fontWeight: 600 }} data-testid="dp-tp-note">Routing via {effTp.tp_name || effTp.tp_code} · booked on {effTp.tp_code}, end customer {selName}. Transfer-party rates applied.</span>
                : <span className="muted" style={{ fontSize: 11, width: "100%" }} data-testid="dp-tp-suppressed">Transfer party off — dispatching directly to {effTp.customer_name || selName} at PO rates.</span>}
            </>
          ) : hasApprovedTp ? (
            <>
              <span style={{ fontSize: 12.5, color: useTpStandalone ? "#0F766E" : "#9CA3AF", fontWeight: 700 }} data-testid="dp-tp-standalone-label">
                Transfer pricing available for {selName}
              </span>
              <span style={{ flex: 1 }} />
              {tpOptions.length > 0 && <ManualTpPicker tpOptions={tpOptions} onPick={(o) => { setManualTp({ tp_code: o.tp_code, tp_name: o.tp_name }); setTpOn(true); setUseTpStandalone(false); }} />}
              <span className="muted" style={{ fontSize: 11, fontWeight: 700 }} title="Price these lines at the approved transfer (Partyi) rate instead of the PO price">Use transfer pricing?</span>
              <div style={{ display: "flex", border: "1px solid var(--jde-border)", borderRadius: 999, overflow: "hidden" }}>
                <span data-testid="dp-tp-standalone-no" onClick={() => setUseTpStandalone(false)} style={{ padding: "4px 14px", fontSize: 11, fontWeight: 700, cursor: "pointer", background: !useTpStandalone ? "#6B7280" : "white", color: !useTpStandalone ? "white" : "#6B7280" }}>No</span>
                <span data-testid="dp-tp-standalone-yes" onClick={() => setUseTpStandalone(true)} style={{ padding: "4px 14px", fontSize: 11, fontWeight: 700, cursor: "pointer", background: useTpStandalone ? "#0F766E" : "white", color: useTpStandalone ? "white" : "#6B7280" }}>Yes</span>
              </div>
              {useTpStandalone
                ? <span style={{ fontSize: 11, width: "100%", color: "#0F766E", fontWeight: 600 }} data-testid="dp-tp-standalone-note">Direct dispatch to {selName} · approved transfer rates applied (lines without a valid transfer price are flagged).</span>
                : <span className="muted" style={{ fontSize: 11, width: "100%" }}>Direct dispatch to {selName} at PO rates.</span>}
            </>
          ) : (
            <>
              <span className="muted" style={{ fontSize: 12 }}>Direct dispatch to {selName}</span>
              <span style={{ flex: 1 }} />
              {tpOptions.length > 0 && <ManualTpPicker tpOptions={tpOptions} onPick={(o) => { setManualTp({ tp_code: o.tp_code, tp_name: o.tp_name }); setTpOn(true); }} />}
            </>
          )}
        </div>}

        {pcode && <div style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "center", flexWrap: "wrap" }}>
          <span className="muted" style={{ fontSize: 12, fontWeight: 700 }}>SHOW:</span>
          {[["overdue", "Overdue"], ["30", "Due \u2264 30d"], ["90", "Due \u2264 90d"], ["all", "All open"]].map(([k, l]) => (
            <span key={k} style={chip(filter === k)} onClick={() => setFilter(k)} data-testid={`dp-filter-${k}`}>{l}</span>))}
        </div>}

        <div className="dp-3col">
          {/* LEFT: PO or Item picker */}
          <div className="jde-panel" data-testid="dp-picker">
            <div className="jde-panel-header"><div className="jde-panel-title">{mode === "po" ? <><FileText size={13} weight="bold" /> Open POs</> : <><Package size={13} weight="bold" /> Open items</>}</div>
              <span className="muted" style={{ fontSize: 11 }}>{loadingList ? "…" : (mode === "po" ? filteredPos.length : filteredItems.length)}</span></div>
            <div className="jde-panel-body" style={{ padding: 6, maxHeight: 560, overflowY: "auto" }}>
              {!pcode && <div className="muted" style={{ fontSize: 12, padding: 8 }}>Select a customer.</div>}
              {pcode && loadingList && (
                <div data-testid="dp-list-loading" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10, padding: "48px 12px", color: "var(--omh-text-muted,#6b7280)" }}>
                  <CircleNotch size={26} weight="bold" className="omh-spin" />
                  <span style={{ fontSize: 12.5, fontWeight: 600 }}>{mode === "po" ? "Loading POs…" : "Loading items…"}</span>
                </div>
              )}
              {pcode && !loadingList && mode === "po" && filteredPos.length === 0 && (
                <div className="muted" style={{ fontSize: 12, padding: 8 }}>No open POs.</div>
              )}
              {pcode && !loadingList && mode === "item" && filteredItems.length === 0 && (
                <div className="muted" style={{ fontSize: 12, padding: 8 }}>No open items.</div>
              )}
              {!loadingList && mode === "po" && filteredPos.map((p) => (
                <div key={p.po} data-testid={`dp-po-${p.po}`} onClick={() => setSelKey(p.po)} style={{ border: "1px solid " + (selKey === p.po ? "var(--omh-primary,#2A5C9B)" : "var(--jde-border)"), background: selKey === p.po ? "#EAF1FB" : "white", borderRadius: 6, padding: "8px 10px", marginBottom: 7, cursor: "pointer" }}>
                  <div style={{ display: "flex", justifyContent: "space-between" }}><strong className="text-mono" style={{ fontSize: 13.5 }}>{p.custpo || p.po}</strong>{p.overdue && <span className="jde-badge" style={{ background: "#FEE2E2", color: "#B91C1C", fontSize: 9 }}>OVERDUE</span>}</div>
                  <div className="muted" style={{ fontSize: 12, marginTop: 3 }}>{p.lines} lines · {moneyC(p.open_value, p.currency)}</div>
                  <div className="muted" style={{ fontSize: 12 }}>earliest due {p.earliest_due || "—"}</div></div>))}
              {!loadingList && mode === "item" && filteredItems.map((it) => (
                <div key={it.icode} data-testid={`dp-item-${it.icode}`} onClick={() => setSelKey(it.icode)} style={{ border: "1px solid " + (selKey === it.icode ? "var(--omh-primary,#2A5C9B)" : "var(--jde-border)"), background: selKey === it.icode ? "#EAF1FB" : "white", borderRadius: 6, padding: "8px 10px", marginBottom: 7, cursor: "pointer" }}>
                  <div style={{ display: "flex", justifyContent: "space-between" }}><strong className="text-mono" style={{ fontSize: 13.5 }}>{it.icode}</strong>{it.overdue && <span className="jde-badge" style={{ background: "#FEE2E2", color: "#B91C1C", fontSize: 9 }}>OVERDUE</span>}</div>
                  <div className="muted" style={{ fontSize: 12, marginTop: 3 }}><strong style={{ color: "#374151" }}>{it.po_count} POs</strong> · {fmt(it.open)} open · ~{it.est_containers} {it.pack_basis === "carton" ? "cartons" : "pallets"}</div>
                  <div className="muted" style={{ fontSize: 12 }}>earliest due {it.earliest_due || "—"}</div></div>))}
            </div>
          </div>

          {/* MIDDLE: lines for the selection */}
          <div className="jde-panel" data-testid="dp-lines-panel">
            <div className="jde-panel-header"><div className="jde-panel-title">{selKey
              ? (mode === "po"
                ? <>PO {(selPo?.custpo || lines[0]?.custpo) || selKey}{((selPo?.custpo || lines[0]?.custpo) && (selPo?.custpo || lines[0]?.custpo) !== selKey) && <span className="muted" style={{ fontWeight: 400, fontSize: 10.5, marginLeft: 6 }}>· SAP {selKey}</span>}</>
                : `${selKey} — across POs`)
              : (editingId ? <><PencilSimple size={13} weight="bold" /> Editing plan <span className="text-mono" style={{ fontWeight: 800, marginLeft: 4 }}>DP-{editingId.slice(-6).toUpperCase()}</span> — adjust dispatch qty</> : "Select on the left")}</div>
              <span className="muted" style={{ fontSize: 11 }}>{selKey ? (loadingLines ? "…" : `${lines.length} lines`) : (editingId ? `${cart.length} lines` : "0 lines")}</span></div>
            <div className="jde-panel-body" style={{ padding: 0, overflowX: "auto" }}>
              {!selKey && !editingId && <div className="muted" style={{ padding: 18, fontSize: 12 }}>{mode === "po" ? "Pick a PO to see its release lines." : "Pick an item to see it across all POs."}</div>}
              {selKey && loadingLines && (
                <div data-testid="dp-lines-loading" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10, padding: "48px 12px", color: "var(--omh-text-muted,#6b7280)" }}>
                  <CircleNotch size={26} weight="bold" className="omh-spin" />
                  <span style={{ fontSize: 12.5, fontWeight: 600 }}>{mode === "po" ? "Loading PO lines…" : "Loading item lines…"}</span>
                </div>
              )}
              {!selKey && editingId && cart.length > 0 && (
                <>
                  <div style={{ padding: "9px 12px", background: "#FFF7ED", borderBottom: "1px solid #FED7AA", fontSize: 11.5, color: "#9A3412" }} data-testid="dp-editing-hint">
                    Editing this plan's lines. Change the <strong>Disp Qty</strong> below, or pick a PO on the left to add more lines. Then click <strong>Update Dispatch Plan</strong>.
                  </div>
                  <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 700 }}>
                    <thead><tr>
                      <th style={th}>PO</th><th style={th}>Item / Rel</th><th style={th}>Deliv</th>
                      <th style={{ ...th, textAlign: "right" }}>Bal</th><th style={{ ...th, textAlign: "right" }}>Rate</th>
                      <th style={{ ...th, textAlign: "right", background: "#EAF1FB", color: "#2A5C9B" }}>Disp Qty</th>
                      <th style={{ ...th, textAlign: "right" }}>Amount</th><th style={th}></th>
                    </tr></thead>
                    <tbody>
                      {cart.map((c) => (
                        <tr key={cartKey(c)} data-testid={`dp-edit-line-${cartKey(c)}`} style={{ background: c.disp_qty > 0 ? "#F0FDF4" : "white" }}>
                          <td style={td}><strong className="text-mono" style={{ fontSize: 12 }}>{c.custpo || c.po}</strong></td>
                          <td style={td}><span className="text-mono" style={{ fontWeight: 600 }}>{c.icode}</span> <span className="jde-badge confirmed" style={{ fontSize: 9 }}>{c.release}</span></td>
                          <td style={td}><span className="text-mono" style={{ fontSize: 12, color: daysTo(c.deliv_date) < 0 ? "#B91C1C" : "inherit", fontWeight: daysTo(c.deliv_date) < 0 ? 700 : 400 }}>{fmtDate(c.deliv_date)}</span></td>
                          <td style={{ ...td, textAlign: "right", fontWeight: 700 }} className="text-mono">{fmt(c.bal_qty)}</td>
                          <td style={{ ...td, textAlign: "right" }} className="text-mono">{c.rate}</td>
                          <td style={{ ...td, textAlign: "right", background: "#F8FBFF" }}><input type="number" min="0" max={c.bal_qty} value={c.disp_qty || ""} placeholder="0" onChange={(e) => updateCartQty(c, e.target.value)} data-testid={`dp-edit-qty-${cartKey(c)}`} style={numIn} /></td>
                          <td style={{ ...td, textAlign: "right" }} className="text-mono">{c.disp_qty > 0 ? moneyC(c.disp_qty * c.rate, c.currency) : "—"}</td>
                          <td style={{ ...td, textAlign: "center" }}><button onClick={() => removeCart(c)} data-testid={`dp-edit-rm-${cartKey(c)}`} className="omh-icon-btn" style={{ color: "#B91C1C" }}><Trash size={13} weight="bold" /></button></td>
                        </tr>))}
                    </tbody>
                  </table>
                </>
              )}
              {!selKey && editingId && cart.length === 0 && <div className="muted" style={{ padding: 18, fontSize: 12 }}>This plan has no lines. Pick a PO on the left to add lines.</div>}
              {selKey && !loadingLines && lines.length > 0 && (
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 700 }}>
                  <thead><tr>
                    {mode === "item" && <th style={th}>PO</th>}
                    <th style={th}>{mode === "po" ? "Item / Rel" : "Rel"}</th><th style={th}>Deliv</th>
                    <th style={{ ...th, textAlign: "right" }}>Bal</th><th style={{ ...th, textAlign: "right" }}>{transferPrice ? "Rate (TP)" : "Rate"}</th>
                    <th style={th}>Pack</th>
                    <th style={{ ...th, textAlign: "right", background: "#EAF1FB", color: "#2A5C9B" }}>Disp Qty</th>
                    <th style={{ ...th, textAlign: "right" }}>Amount</th>
                  </tr></thead>
                  <tbody>
                    {lines.map((l) => {
                      const k = cartKey(l); const q = qty[k] || 0; const inCart = cart.find((c) => cartKey(c) === k);
                      return (
                        <tr key={k} data-testid={`dp-line-${k}`} style={{ background: q > 0 ? "#F0FDF4" : "white" }}>
                          {mode === "item" && <td style={td}><strong className="text-mono">{l.custpo || l.po}</strong></td>}
                          <td style={td}>{mode === "po" ? <><span className="text-mono" style={{ fontWeight: 600 }}>{l.icode}</span> <span className="jde-badge confirmed" style={{ fontSize: 9 }}>{l.release}</span></> : <span className="jde-badge confirmed" style={{ fontSize: 9 }}>{l.release}</span>}{inCart && <span className="jde-badge shipped" style={{ fontSize: 8, marginLeft: 4 }}>in plan</span>}</td>
                          <td style={td}><span className="text-mono" style={{ fontSize: 12, color: daysTo(l.deliv_date) < 0 ? "#B91C1C" : "inherit", fontWeight: daysTo(l.deliv_date) < 0 ? 700 : 400 }}>{fmtDate(l.deliv_date)}</span></td>
                          <td style={{ ...td, textAlign: "right", fontWeight: 700 }} className="text-mono">{fmt(l.bal_qty)}</td>
                          <td style={{ ...td, textAlign: "right" }} className="text-mono" data-testid={`dp-rate-${k}`}>{transferPrice
                            ? (l.needs_transfer_price
                              ? <span className="jde-badge" style={{ fontSize: 9, background: "#FEE2E2", color: "#B91C1C", fontWeight: 700 }} title={l.tp_reason === "expired" ? `Transfer price expired on ${l.tp_valid_until}` : "No approved transfer price — set it in Settings"}>{l.tp_reason === "expired" ? "Expired" : "No TP"}</span>
                              : <><span style={{ fontWeight: 700, color: "#0F766E" }}>{l.transfer_rate}</span><span className="muted" data-testid={`dp-po-rate-${k}`} style={{ fontSize: 9, marginLeft: 4, textDecoration: "line-through" }}>{l.po_rate}</span></>)
                            : l.rate}</td>
                          <td style={td}>{l.needs_master
                            ? <button data-testid={`dp-needs-master-${l.icode}`} onClick={() => setMasterItem(l)} className="jde-badge" style={{ fontSize: 9, background: "#FEF3C7", color: "#92400E", border: "none", cursor: "pointer", fontWeight: 700 }} title="Packing/weights missing in master — click to add">Set details</button>
                            : <span className="jde-badge" style={{ fontSize: 9, background: "#EEF2F7", color: "#374151" }}>{packLabel(l)}</span>}</td>
                          <td style={{ ...td, textAlign: "right", background: "#F8FBFF" }}><input type="number" min="0" max={l.bal_qty} value={q || ""} placeholder="0" onChange={(e) => setQ(l, e.target.value)} data-testid={`dp-qty-${k}`} style={numIn} /></td>
                          <td style={{ ...td, textAlign: "right" }} className="text-mono">{transferPrice && l.needs_transfer_price ? <span className="muted" style={{ fontSize: 10 }}>blocked</span> : (q > 0 ? moneyC(q * l.rate, l.currency) : "—")}</td>
                        </tr>);
                    })}
                  </tbody>
                </table>
              )}
              {selKey && !loadingLines && lines.length > 0 && <div style={{ padding: "9px 10px", display: "flex", justifyContent: "flex-end" }}>
                <button data-testid="dp-add-btn" onClick={() => addToCart()} style={{ padding: "7px 14px", border: "none", borderRadius: 6, background: "#0F766E", color: "white", fontWeight: 700, fontSize: 12, cursor: "pointer", display: "flex", gap: 6, alignItems: "center" }}><ListChecks size={14} weight="bold" /> Add to dispatch plan</button>
              </div>}
            </div>
          </div>

          {/* RIGHT: cart + pallets + staged */}
          <div className="dp-cart">
            <div className="jde-panel" data-testid="dp-cart-panel">
              <div className="jde-panel-header" style={{ background: editingId ? "#B45309" : "var(--omh-primary,#2A5C9B)" }}><div className="jde-panel-title" style={{ color: "white" }}><Truck size={14} weight="bold" /> {editingId ? <>Editing Plan <span className="text-mono" data-testid="dp-edit-planid" style={{ fontWeight: 800, marginLeft: 6, background: "rgba(255,255,255,0.22)", padding: "1px 8px", borderRadius: 999, fontSize: 12 }}>DP-{editingId.slice(-6).toUpperCase()}</span></> : "Dispatch Plan"}</div></div>
              <div className="jde-panel-body">
                {selName && <div className="muted" style={{ fontSize: 10.5, marginBottom: 6 }}>{selName} · {hdr.plant} · {fmtDate(hdr.dispatch_date)}{transferPrice && <span className="jde-badge" style={{ marginLeft: 6, fontSize: 8.5, background: "#CCFBF1", color: "#0F766E", fontWeight: 700 }}>TRANSFER PRICE</span>}</div>}
                {groups.length > 0 && <div data-testid="dp-consolidate-box" style={{ marginBottom: 10, background: "#ECFDF5", border: "1px solid #6EE7B7", borderRadius: 8, padding: "9px 11px" }}>
                  <div style={{ fontSize: 11.5, fontWeight: 800, color: "#065F46", marginBottom: 5, display: "flex", alignItems: "center", gap: 6 }}><Stack size={13} weight="bold" /> COMBINE QTY ACROSS PO LINES <span style={{ fontWeight: 600, color: "#059669" }}>· on by default</span></div>
                  {groups.map((g) => (
                    <label key={g.icode} data-testid={`dp-consolidate-${g.icode}`} style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 11.5, padding: "3px 0", cursor: "pointer", color: "#065F46" }}>
                      <input type="checkbox" checked={consolidate.includes(g.icode)} onChange={() => toggleConsolidate(g.icode)} data-testid={`dp-consolidate-chk-${g.icode}`} />
                      <span className="text-mono" style={{ fontWeight: 700 }}>{g.icode}</span><span style={{ color: "#047857" }}>— {g.count} PO lines combined (untick to keep separate)</span>
                    </label>))}
                </div>}
                {cart.length === 0 && <div className="muted" style={{ fontSize: 12 }}>Enter qty and click <strong>Add to dispatch plan</strong>.</div>}
                {cart.map((c) => (
                  <div key={cartKey(c)} data-testid={`dp-cart-${cartKey(c)}`} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: "1px solid #EEF1F4" }}>
                    <div><span className="text-mono" style={{ fontWeight: 600, fontSize: 11.5 }}>{c.icode}</span><div className="muted" style={{ fontSize: 10 }}>{c.custpo || c.po} · {c.release} · {fmt(c.disp_qty)} EA</div></div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}><span className="text-mono" style={{ fontSize: 11.5, fontWeight: 600 }}>{moneyC(c.disp_qty * c.rate, c.currency)}</span>
                      <button onClick={() => removeCart(c)} data-testid={`dp-rm-${cartKey(c)}`} className="omh-icon-btn" style={{ color: "#B91C1C" }}><Trash size={12} weight="bold" /></button></div>
                  </div>))}
                {cart.length > 0 && <>
                  <div style={{ marginTop: 10, background: "#F8FAFC", border: "1px solid var(--jde-border)", borderRadius: 6, padding: "10px 12px" }}>
                    {[["Total units", fmt(preview?.tot_qty ?? cart.reduce((a, c) => a + c.disp_qty, 0)) + " EA", Package],
                      ["Pallets / Cartons", `${preview?.tot_pallets > 0 ? preview.tot_pallets : "—"} / ${preview?.tot_cartons_actual > 0 ? preview.tot_cartons_actual : "—"}`, Stack],
                      ["Boxes (rollup)", fmt(preview?.tot_boxes ?? 0), null],
                      ["Net / Gross", preview ? `${wt(preview.tot_net_wt)} / ${wt(preview.tot_gross_wt)}` : "—", null],
                      ["Total value", moneyC(preview?.tot_amount ?? cart.reduce((a, c) => a + c.disp_qty * c.rate, 0), custCurrency), null]].map(([k, v, Icon]) => (
                      <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "3px 0" }}>
                        <span style={{ fontSize: 11, color: "#6B7280", display: "flex", gap: 5, alignItems: "center" }}>{Icon && <Icon size={12} weight="bold" />}{k}</span>
                        <span className="text-mono" style={{ fontSize: 13, fontWeight: 700 }}>{v}</span></div>))}
                  </div>
                  <button data-testid="dp-save-btn" onClick={save} disabled={saving} style={{ width: "100%", marginTop: 12, padding: "10px 14px", border: "none", borderRadius: 6, background: editingId ? "#B45309" : "var(--omh-primary,#2A5C9B)", color: "white", fontWeight: 700, fontSize: 13, cursor: "pointer", display: "flex", gap: 8, alignItems: "center", justifyContent: "center" }}><FloppyDisk size={16} weight="bold" /> {saving ? "Saving…" : (editingId ? "Update Dispatch Plan" : "Save Dispatch Plan")}</button>
                  {editingId && <button data-testid="dp-cancel-edit-btn" onClick={cancelEdit} style={{ width: "100%", marginTop: 6, padding: "6px 14px", border: "1px solid var(--jde-border)", borderRadius: 6, background: "white", color: "#6B7280", fontWeight: 700, fontSize: 11.5, cursor: "pointer" }}>Cancel edit</button>}
                </>}
              </div>
            </div>

            {/* consolidation + pallet breakdown */}
            {cart.length > 0 && (
              <div className="jde-panel" data-testid="dp-pallet-panel">
                <div className="jde-panel-header"><div className="jde-panel-title"><Stack size={13} weight="bold" /> Pallet / Carton layout</div></div>
                <div className="jde-panel-body" style={{ padding: 10 }}>
                  {preview?.pallets?.length > 0 ? <div style={{ maxHeight: 240, overflowY: "auto" }}>
                    {preview.pallets.map((p) => { const shared = (p.fills || []).length > 1; return (
                      <div key={p.pallet_no} data-testid={`dp-pallet-${p.pallet_no}`} style={{ borderBottom: "1px solid #EEF1F4", padding: "5px 0", fontSize: 11, background: shared ? "#FFFBEB" : undefined }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span style={{ fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 6 }}>
                            {p.unit} {p.pallet_no} of {preview.pallets.length} <span className="text-mono muted" style={{ fontSize: 10 }}>{p.icode}</span>
                            {shared && <span data-testid={`dp-pallet-shared-badge-${p.pallet_no}`} style={{ fontSize: 8.5, fontWeight: 800, color: "#fff", background: "#F59E0B", borderRadius: 4, padding: "1px 5px", letterSpacing: 0.3 }}>SHARED · {p.fills.length} POs</span>}
                          </span>
                          <span className="text-mono" style={{ color: p.filled >= p.cap ? "#0F766E" : "#B45309", fontWeight: 600 }}>{fmt(p.filled)}/{fmt(p.cap)}</span></div>
                        <div data-testid={`dp-pallet-fills-${p.pallet_no}`} style={{ fontSize: 10.5, marginTop: 2, color: shared ? "#B45309" : "#94A3B8", fontWeight: shared ? 700 : 400 }}>
                          {(p.fills || []).map((f) => `${f.custpo || f.po} \u00d7${fmt(f.qty)}`).join("  +  ")}</div>
                      </div>); })}
                  </div> : <div className="muted" style={{ fontSize: 11 }}>Computing…</div>}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* staged plans — full-width standardized table */}
        <div className="jde-panel" data-testid="dp-saved-panel" style={{ marginTop: 12 }}>
          <div className="jde-panel-header">
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div className="jde-panel-title"><CloudArrowUp size={14} weight="bold" /> {planTab === "posted" ? "Posted plans" : "Staged plans"}</div>
              <div className="dp-seg dp-seg-sm" data-testid="dp-plan-tabs">
                <button type="button" data-testid="dp-tab-staged" className={planTab === "staged" ? "active" : ""} onClick={() => setPlanTab("staged")}>Staged</button>
                <button type="button" data-testid="dp-tab-posted" className={planTab === "posted" ? "active" : ""} onClick={() => setPlanTab("posted")}>Posted</button>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div className="dp-search" style={{ height: 30 }}>
                <MagnifyingGlass size={13} weight="bold" style={{ color: "var(--omh-text-muted)", flex: "0 0 auto" }} />
                <input value={planSearch} onChange={(e) => setPlanSearch(e.target.value)} placeholder="Search plan ID / customer…" data-testid="dp-plan-search" style={{ width: 190 }} />
              </div>
              <span className="muted" style={{ fontSize: 11, whiteSpace: "nowrap" }}>{visiblePlans.length} plan{visiblePlans.length === 1 ? "" : "s"}</span>
            </div>
          </div>
          <div className="jde-panel-body" style={{ padding: 0, overflowX: "auto" }}>
            {visiblePlans.length === 0
              ? <div className="muted" style={{ fontSize: 12, padding: 16 }} data-testid="dp-saved-empty">{planSearch.trim() ? `No plans match “${planSearch.trim()}”.` : (planTab === "posted" ? "No posted plans." : "No staged plans yet.")}</div>
              : <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 940 }}>
                  <thead><tr>
                    <th style={th}>Plan ID</th>
                    <th style={th}>Customer</th>
                    <th style={th}>Status</th>
                    <th style={th}>Plant</th>
                    <th style={{ ...th, textAlign: "right" }}>Qty</th>
                    <th style={{ ...th, textAlign: "right" }}>Amount</th>
                    <th style={th}>Voc#</th>
                    <th style={th}>Date</th>
                    <th style={th}>By</th>
                    <th style={{ ...th, textAlign: "right", width: 1 }}>Actions</th>
                  </tr></thead>
                  <tbody>
                    {visiblePlans.map((p) => (
                      <tr key={p.id} data-testid={`dp-saved-${p.id}`}>
                        <td style={{ ...td, verticalAlign: "top" }}>
                          <span className="text-mono" style={{ fontWeight: 700, fontSize: 12 }} data-testid={`dp-saved-planid-${p.id}`} title={`Plan ID: ${p.id}`}>DP-{p.id.slice(-6).toUpperCase()}</span>
                        </td>
                        <td style={{ ...td, verticalAlign: "top" }}>
                          <strong style={{ fontSize: 13 }}>{p.customer_name || p.pcode}</strong>
                          {(p.transfer_price || p.transfer_party) && <div style={{ display: "flex", gap: 4, marginTop: 4, flexWrap: "wrap" }}>
                            {p.transfer_price && <span className="jde-badge" style={{ fontSize: 8.5, background: "#CCFBF1", color: "#0F766E", borderColor: "#5EEAD4" }}>TP</span>}
                            {p.transfer_party && <span className="jde-badge" data-testid={`dp-saved-tp-${p.id}`} style={{ fontSize: 8.5, background: "#0F766E", color: "white", borderColor: "#0F766E", textTransform: "none", letterSpacing: 0 }} title={`Transfer party: ${p.tp_name || p.tp_code}`}>↳ {p.tp_name || p.tp_code}</span>}
                          </div>}
                        </td>
                        <td style={{ ...td, verticalAlign: "top" }}>
                          <span className={`jde-badge ${p.status === "posted" ? "shipped" : "pending"}`} style={{ fontSize: 9 }}>{p.status}</span>
                          {p.status === "posted" && <div style={{ fontSize: 10, marginTop: 4 }}>
                            {p.needs_repost && <div style={{ color: "#B45309", fontWeight: 700, marginBottom: 2 }} data-testid={`dp-needsrepost-${p.id}`}>Re-post to update</div>}
                            {p.invoiced ? <span style={{ color: "#166534", fontWeight: 700 }} data-testid={`dp-inv-${p.id}`}>Invoiced #{p.inv_no}{p.inv_date ? ` · ${p.inv_date}` : ""}</span>
                              : p.invoiced === false ? <span style={{ color: "#B45309", fontWeight: 700 }} data-testid={`dp-inv-${p.id}`}>Not invoiced</span>
                              : <span className="muted" data-testid={`dp-inv-${p.id}`}>Invoice status n/a</span>}
                          </div>}
                        </td>
                        <td style={{ ...td, verticalAlign: "top" }}><span className="text-mono" style={{ fontWeight: 600, fontSize: 12 }}>{p.plant || "—"}</span></td>
                        <td style={{ ...td, textAlign: "right", verticalAlign: "top", fontSize: 12 }} className="text-mono">{fmt(p.tot_qty)} <span className="muted" style={{ fontSize: 10 }}>EA</span></td>
                        <td style={{ ...td, textAlign: "right", verticalAlign: "top", fontWeight: 700, fontSize: 12 }} className="text-mono">{moneyC(p.tot_amount, p.currency)}</td>
                        <td style={{ ...td, verticalAlign: "top" }}><span className="text-mono" style={{ fontSize: 12 }}>{p.voc_no || "—"}</span></td>
                        <td style={{ ...td, verticalAlign: "top" }}><span className="text-mono" style={{ fontSize: 12 }}>{fmtDate(p.dispatch_date)}</span></td>
                        <td style={{ ...td, verticalAlign: "top" }}><span className="muted" style={{ fontSize: 12 }}>{p.created_by || "—"}</span></td>
                        <td style={{ ...td, verticalAlign: "top", textAlign: "right", whiteSpace: "nowrap" }}>
                          <div style={{ display: "inline-flex", gap: 6, alignItems: "center", justifyContent: "flex-end" }}>
                            <button data-testid={`dp-summary-${p.id}`} type="button" onClick={() => openSummary(p)} disabled={openingSummary === p.id} style={{ ...actBtn, color: "#2A5C9B", cursor: openingSummary === p.id ? "wait" : "pointer", opacity: openingSummary === p.id ? 0.6 : 1, transition: "opacity .15s ease" }} title="Dispatch summary / email preview"><Envelope size={13} weight="bold" /> {openingSummary === p.id ? "Opening…" : "Email"}</button>
                            {(p.status !== "posted" || p.invoiced !== true) && <button data-testid={`dp-edit-${p.id}`} onClick={() => editPlan(p)} style={{ ...actBtn, color: "#B45309" }} title={p.status === "posted" ? "Edit this posted plan (re-post to update MS SQL)" : "Edit this draft plan"}><PencilSimple size={13} weight="bold" /> Edit</button>}
                            {p.status !== "posted" && <button data-testid={`dp-post-${p.id}`} onClick={() => postPlan(p)} disabled={posting === p.id} style={{ ...actBtn, border: "none", background: "#0F766E", color: "white" }} title="Post this dispatch to the MS SQL portal">{posting === p.id ? "Posting…" : <>Post to MS SQL <ArrowRight size={12} weight="bold" /></>}</button>}
                            {p.status === "posted" && p.invoiced !== true && <button data-testid={`dp-repost-${p.id}`} onClick={() => repostPlan(p)} disabled={posting === p.id} style={{ ...actBtn, border: "none", background: p.needs_repost ? "#B45309" : "#0F766E", color: "white" }} title="Rewrite this voucher in MS SQL with the current plan">{posting === p.id ? "Re-posting…" : <>Re-post <ArrowRight size={12} weight="bold" /></>}</button>}
                            {(p.status !== "posted" || p.invoiced === false) && <button data-testid={`dp-del-${p.id}`} onClick={() => delPlan(p)} style={{ ...actBtn, color: "#B91C1C", padding: "5px 8px" }} title={p.status === "posted" ? "Delete dispatch from system (no invoice yet)" : "Delete draft"}><Trash size={13} weight="bold" /></button>}
                          </div>
                        </td>
                      </tr>))}
                  </tbody>
                </table>}
          </div>
        </div>

      </div>
      {summary && <DispatchSummaryModal data={summary} inv={summaryInv} setInv={setSummaryInv} onClose={() => setSummary(null)} />}
      {masterItem && <ItemMasterModal item={masterItem} onSave={saveMaster} onClose={() => setMasterItem(null)} />}
    </Layout>
  );
}

// ── Dispatch-completion summary / email preview ──────────────────────────────
// Chip-based recipient editor. Shows current addresses as removable chips, lets you type new ones
// (Enter / comma / blur to add), and — on focus — surfaces a dropdown of ALL available email IDs
// (the configured defaults) that aren't already selected, so any of them can be added in one click.
const _isEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(e || "").trim());
function RecipientPicker({ label, value, onChange, directory = [], testid, optional = false }) {
  const [draft, setDraft] = useState("");
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const h = (ev) => { if (boxRef.current && !boxRef.current.contains(ev.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const add = (raw) => {
    const parts = String(raw).split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);
    const next = [...value];
    parts.forEach((p) => {
      if (!next.some((v) => v.toLowerCase() === p.toLowerCase())) next.push(p);
    });
    if (next.length !== value.length) onChange(next);
    setDraft("");
  };
  const remove = (e) => onChange(value.filter((v) => v !== e));
  const available = directory.filter((d) => !value.some((v) => v.toLowerCase() === d.toLowerCase()));

  return (
    <div ref={boxRef} style={{ position: "relative", flex: optional ? "1 1 300px" : "1 1 320px", display: "flex", flexDirection: "column", gap: 3 }}>
      <span style={{ fontSize: 10, fontWeight: 700, color: "#6B7280" }}>{label}</span>
      <div
        data-testid={`${testid}-box`}
        onClick={() => setOpen(true)}
        style={{ display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center", minHeight: 34, border: "1px solid var(--jde-border)", borderRadius: 6, padding: "4px 6px", background: "white", cursor: "text" }}
      >
        {value.map((e) => (
          <span key={e} data-testid={`${testid}-chip-${e}`} style={{ display: "inline-flex", alignItems: "center", gap: 4, background: _isEmail(e) ? "#EAF1FB" : "#FEE2E2", color: _isEmail(e) ? "#1E3A8A" : "#B91C1C", borderRadius: 999, padding: "2px 6px 2px 9px", fontSize: 11, fontWeight: 600, maxWidth: "100%" }} title={e}>
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 220 }}>{e}</span>
            <button type="button" data-testid={`${testid}-remove-${e}`} onClick={(ev) => { ev.stopPropagation(); remove(e); }} aria-label={`Remove ${e}`}
              style={{ display: "inline-flex", border: "none", background: "transparent", cursor: "pointer", color: "inherit", padding: 0, lineHeight: 0 }}>
              <X size={11} weight="bold" />
            </button>
          </span>
        ))}
        <input
          data-testid={testid}
          value={draft}
          onFocus={() => setOpen(true)}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === "," || e.key === ";") { e.preventDefault(); if (draft.trim()) add(draft); }
            else if (e.key === "Backspace" && !draft && value.length) { remove(value[value.length - 1]); }
          }}
          onBlur={() => { if (draft.trim()) add(draft); }}
          placeholder={value.length ? "" : (optional ? "Add CC email…" : "email1@co.in, email2@co.in")}
          style={{ flex: "1 1 90px", minWidth: 90, border: "none", outline: "none", fontSize: 12, padding: "3px 2px", color: "#1f2937", background: "transparent" }}
        />
      </div>
      {open && available.length > 0 && (
        <div data-testid={`${testid}-dropdown`} style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 30, marginTop: 3, background: "white", border: "1px solid var(--jde-border)", borderRadius: 6, boxShadow: "0 10px 28px rgba(0,0,0,.14)", maxHeight: 190, overflowY: "auto" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "5px 9px", fontSize: 9.5, fontWeight: 800, color: "#94A3B8", textTransform: "uppercase", letterSpacing: ".04em", borderBottom: "1px solid #F1F5F9" }}>
            <span>Available email IDs</span>
            <button type="button" data-testid={`${testid}-add-all`} onMouseDown={(e) => e.preventDefault()} onClick={() => onChange([...value, ...available])}
              style={{ border: "none", background: "transparent", color: "#2A5C9B", fontSize: 10, fontWeight: 800, cursor: "pointer" }}>Add all</button>
          </div>
          {available.map((d) => (
            <button key={d} type="button" data-testid={`${testid}-add-${d}`} onMouseDown={(e) => e.preventDefault()} onClick={() => add(d)}
              style={{ display: "block", width: "100%", textAlign: "left", padding: "7px 9px", border: "none", background: "white", fontSize: 12, color: "#1f2937", cursor: "pointer" }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#F1F5F9")} onMouseLeave={(e) => (e.currentTarget.style.background = "white")}>
              + {d}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


function DispatchSummaryModal({ data, inv, setInv, onClose }) {
  const c = data.currency || "USD";
  const noun = ({ Pallet: "Pallet", Carton: "Carton" })[data.pack_label] || "Container";
  const consignee = data.consignee
    ? [data.consignee.consignee, data.consignee.address].filter(Boolean).join(", ")
    : (data.consignee_code || "");

  const [to, setTo] = useState([]);       // recipient chips
  const [cc, setCc] = useState([]);       // cc chips
  const [dir, setDir] = useState([]);     // directory of all available email IDs (to+cc defaults)
  const [sending, setSending] = useState(false);
  const [editText, setEditText] = useState(false);
  const [poDocs, setPoDocs] = useState([]);          // per-PO source-doc status for this dispatch
  const [uploadingPo, setUploadingPo] = useState(""); // po_number currently uploading
  const [confirmMissing, setConfirmMissing] = useState(null); // [po_number,...] awaiting send confirm
  const [attachments, setAttachments] = useState([]); // ad-hoc staged files for this email
  const [uploadingGeneral, setUploadingGeneral] = useState(false);
  const fileInputRef = useRef(null);
  const generalInputRef = useRef(null);
  const uploadTargetPo = useRef("");
  const [intro, setIntro] = useState(`Below are the details of '${data.customer_name}' shipment, please prepare the invoice accordingly.\nPO copies are linked below for your reference.`);
  const [notes, setNotes] = useState(
    (data.note_lines || []).map((n) => (n.includes("move from") ? `This shipment will move from ${data.plant}` : n))
  );

  useEffect(() => {
    api.get("/settings/dispatch-email")
      .then(({ data: s }) => {
        const dto = (s.default_to || []).map((x) => String(x).trim()).filter(Boolean);
        const dcc = (s.default_cc || []).map((x) => String(x).trim()).filter(Boolean);
        setTo(dto);
        setCc(dcc);
        // "All available email IDs" the CC/TO pickers can offer = every configured default address.
        const uniq = [];
        [...dto, ...dcc].forEach((e) => {
          if (!uniq.some((u) => u.toLowerCase() === e.toLowerCase())) uniq.push(e);
        });
        setDir(uniq);
      })
      .catch(() => {});
    loadPoDocs();
    loadAttachments();
  }, []);

  const loadPoDocs = () => {
    if (!data._planId) return;
    api.get(`/dispatch/plan/${data._planId}/po-docs`)
      .then(({ data: r }) => setPoDocs(r.po_docs || []))
      .catch(() => {});
  };

  const loadAttachments = () => {
    if (!data._planId) return;
    api.get(`/dispatch/plan/${data._planId}/attachments`)
      .then(({ data: r }) => setAttachments(r.attachments || []))
      .catch(() => {});
  };

  const onGeneralFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingGeneral(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      await api.post(`/dispatch/plan/${data._planId}/attach`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${files.length} file(s) attached`);
      loadAttachments();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Attach failed");
    } finally { setUploadingGeneral(false); if (generalInputRef.current) generalInputRef.current.value = ""; }
  };

  const removeAttachment = async (id) => {
    try {
      await api.delete(`/dispatch/plan/${data._planId}/attach/${id}`);
      loadAttachments();
    } catch { toast.error("Could not remove"); }
  };

  const pickFileFor = (po) => {
    uploadTargetPo.current = po;
    if (fileInputRef.current) { fileInputRef.current.value = ""; fileInputRef.current.click(); }
  };

  const onFileChosen = async (e) => {
    const f = e.target.files && e.target.files[0];
    const po = uploadTargetPo.current;
    if (!f || !po) return;
    setUploadingPo(po);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/dispatch/po/${encodeURIComponent(po)}/upload-source`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`PO copy attached for ${po}`);
      loadPoDocs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploadingPo(""); uploadTargetPo.current = ""; }
  };

  const send = async (force = false) => {
    if (sending) return;   // guard against a second click while a send is in flight
    const toList = to.map((x) => x.trim()).filter(Boolean);
    const ccList = cc.map((x) => x.trim()).filter(Boolean);
    if (!toList.length) return toast.error("Add at least one recipient in To");
    // Warn if any PO on this dispatch has no copy on file — nothing should go out incomplete.
    const missing = poDocs.filter((d) => !d.has_doc).map((d) => d.po_number);
    if (!force && missing.length) { setConfirmMissing(missing); return; }
    setConfirmMissing(null);
    setSending(true);
    try {
      const r = await api.post(`/dispatch/plan/${data._planId}/email`, {
        to: toList, cc: ccList,
        invoice_no: inv.invoice_no, invoice_date: inv.invoice_date,
        intro, notes: notes.map((n) => n.trim()).filter(Boolean),
      });
      toast.success(`Email sent to ${r.data.to.join(", ")}`);
      onClose();   // close the window on success so the same mail can't be sent twice
    } catch (e) {
      toast.error(e.response?.data?.detail || "Email send failed");
      setSending(false);   // re-enable only on failure (on success the modal unmounts)
    }
  };

  const saveDefaults = async () => {
    const toList = to.map((x) => x.trim()).filter(Boolean);
    const ccList = cc.map((x) => x.trim()).filter(Boolean);
    try {
      await api.put("/settings/dispatch-email", { default_to: toList, default_cc: ccList });
      toast.success("Saved as default recipients");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save defaults"); }
  };

  const th = { padding: "5px 6px", fontSize: 10, fontWeight: 700, color: "#374151", borderBottom: "1px solid #E5C9A8", textAlign: "left", whiteSpace: "nowrap" };
  const td = { padding: "6px 6px", fontSize: 11, borderBottom: "1px solid #EEF1F4", verticalAlign: "top" };
  const tdR = { ...td, textAlign: "right", fontFamily: "var(--jde-font-mono)" };

  const printIt = () => {
    const el = document.getElementById("dp-summary-sheet");
    const w = window.open("", "_blank", "width=1100,height=800");
    w.document.write(`<html><head><title>Dispatch ${data.dispatch_no || ""}</title></head><body style="font-family:Arial,sans-serif;padding:20px">${el.innerHTML}</body></html>`);
    w.document.close(); w.focus(); w.print();
  };

  return (
    <div data-testid="dp-summary-modal" style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.55)", zIndex: 60, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "28px 16px", overflowY: "auto" }}>
      <div style={{ background: "white", borderRadius: 10, width: "min(1180px,100%)", boxShadow: "0 20px 60px rgba(0,0,0,.35)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", borderBottom: "1px solid var(--jde-border)" }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", fontWeight: 700, color: "#2A5C9B" }}><Envelope size={17} weight="bold" /> Dispatch summary — prepare invoice</div>
          <div style={{ display: "flex", gap: 8 }}>
            <button data-testid="dp-summary-print" onClick={printIt} style={{ display: "flex", gap: 5, alignItems: "center", padding: "6px 12px", border: "1px solid var(--jde-border)", borderRadius: 6, background: "white", fontSize: 12, fontWeight: 700, cursor: "pointer" }}><Printer size={14} weight="bold" /> Print</button>
            <button data-testid="dp-summary-close" type="button" onClick={onClose} title="Close" aria-label="Close" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 32, height: 32, border: "1px solid var(--jde-border)", borderRadius: 6, background: "#F1F5F9", color: "#334155", cursor: "pointer", transition: "background-color .15s ease" }}><X size={16} weight="bold" /></button>
          </div>
        </div>

        {/* recipients + send bar */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "flex-end", padding: "12px 16px", background: "#F8FAFC", borderBottom: "1px solid var(--jde-border)" }}>
          <RecipientPicker label="TO" testid="dp-email-to" value={to} onChange={setTo} directory={dir} />
          <RecipientPicker label="CC (optional)" optional testid="dp-email-cc" value={cc} onChange={setCc} directory={dir} />
          <button data-testid="dp-email-send" type="button" onClick={() => send(false)} disabled={sending} style={{ display: "flex", gap: 6, alignItems: "center", padding: "8px 18px", border: "none", borderRadius: 6, background: sending ? "#94A3B8" : "#2A5C9B", color: "white", fontSize: 12.5, fontWeight: 700, cursor: sending ? "default" : "pointer", transition: "background-color .15s ease", userSelect: "none" }}><Envelope size={15} weight="bold" /> {sending ? "Sending…" : "Send Email"}</button>
          <button data-testid="dp-email-edit-text" onClick={() => setEditText((v) => !v)} style={{ display: "flex", gap: 6, alignItems: "center", padding: "8px 12px", border: "1px solid " + (editText ? "#0F766E" : "var(--jde-border)"), borderRadius: 6, background: editText ? "#0F766E" : "white", fontSize: 11.5, fontWeight: 700, color: editText ? "white" : "#475569", cursor: "pointer" }}><PencilSimple size={13} weight="bold" /> {editText ? "Done editing" : "Edit text"}</button>
          <button data-testid="dp-email-save-default" onClick={saveDefaults} style={{ padding: "8px 12px", border: "1px solid var(--jde-border)", borderRadius: 6, background: "white", fontSize: 11.5, fontWeight: 700, color: "#475569", cursor: "pointer" }}>Save as default</button>
        </div>

        {/* PO copies — linked as public URLs in the email; missing ones can be attached (saved back to the PO) */}
        <input ref={fileInputRef} type="file" onChange={onFileChosen}
          accept=".pdf,.jpg,.jpeg,.png,.webp,.rtf,.doc,.docx,.xls,.xlsx,.xlsm,.eml,.txt"
          style={{ display: "none" }} data-testid="dp-po-file-input" />
        {poDocs.length > 0 && (
          <div data-testid="dp-po-copies" style={{ padding: "10px 16px", borderBottom: "1px solid var(--jde-border)", background: "#FCFDFF" }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#6B7280", marginBottom: 6 }}>
              PO COPIES ({poDocs.filter((d) => d.has_doc).length}/{poDocs.length} on file — linked in email)
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {poDocs.map((d) => (
                <div key={d.po_number} data-testid={`dp-po-copy-${d.po_number}`}
                  style={{ display: "flex", alignItems: "center", gap: 7, padding: "5px 9px", borderRadius: 6, fontSize: 11.5,
                    border: "1px solid " + (d.has_doc ? "#BBF7D0" : "#FDE68A"), background: d.has_doc ? "#F0FDF4" : "#FFFBEB" }}>
                  <span style={{ fontFamily: "var(--jde-font-mono)", fontWeight: 700, color: "#334155" }}>{d.po_number}</span>
                  {d.has_doc ? (
                    <>
                      <CheckCircle size={15} weight="fill" color="#16A34A" />
                      <a href={d.url} target="_blank" rel="noopener noreferrer" data-testid={`dp-po-view-${d.po_number}`}
                        style={{ display: "flex", alignItems: "center", gap: 3, color: "#2A5C9B", fontWeight: 700, textDecoration: "none" }}>
                        <LinkSimple size={13} weight="bold" /> View
                      </a>
                    </>
                  ) : (
                    <>
                      <Warning size={15} weight="fill" color="#D97706" />
                      <button data-testid={`dp-po-attach-${d.po_number}`} disabled={uploadingPo === d.po_number}
                        onClick={() => pickFileFor(d.po_number)}
                        style={{ display: "flex", alignItems: "center", gap: 4, padding: "3px 8px", border: "1px solid #D97706", borderRadius: 5,
                          background: "white", color: "#B45309", fontSize: 11, fontWeight: 700, cursor: uploadingPo === d.po_number ? "default" : "pointer" }}>
                        <Paperclip size={12} weight="bold" /> {uploadingPo === d.po_number ? "Uploading…" : "Attach copy"}
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* General attachments — any file(s) added to this dispatch email, regardless of PO line items.
            Useful when a plan has no parsed PO lines (e.g. Excel-intake POs) but the sender still wants
            to attach PO copies / supporting docs. */}
        <input ref={generalInputRef} type="file" multiple onChange={onGeneralFiles}
          accept=".pdf,.jpg,.jpeg,.png,.webp,.rtf,.doc,.docx,.xls,.xlsx,.xlsm,.eml,.txt"
          style={{ display: "none" }} data-testid="dp-general-file-input" />
        <div data-testid="dp-attachments" style={{ padding: "10px 16px", borderBottom: "1px solid var(--jde-border)", background: "#FCFDFF" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#6B7280" }}>
              ATTACHMENTS ({attachments.length}) — added to this email
            </div>
            <button type="button" data-testid="dp-attach-general-btn" disabled={uploadingGeneral}
              onClick={() => generalInputRef.current && generalInputRef.current.click()}
              style={{ display: "flex", alignItems: "center", gap: 5, padding: "4px 10px", border: "1px solid #2A5C9B", borderRadius: 5,
                background: "white", color: "#2A5C9B", fontSize: 11, fontWeight: 700, cursor: uploadingGeneral ? "default" : "pointer" }}>
              <CloudArrowUp size={13} weight="bold" /> {uploadingGeneral ? "Uploading…" : "Attach file(s)"}
            </button>
          </div>
          {attachments.length > 0 ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {attachments.map((a) => (
                <div key={a.id} data-testid={`dp-attachment-${a.id}`}
                  style={{ display: "flex", alignItems: "center", gap: 7, padding: "5px 9px", borderRadius: 6, fontSize: 11.5,
                    border: "1px solid #C7D2E8", background: "#F5F8FD" }}>
                  <FileText size={15} weight="fill" color="#2A5C9B" />
                  <span style={{ color: "#334155", fontWeight: 600, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.name}</span>
                  <button data-testid={`dp-attachment-remove-${a.id}`} onClick={() => removeAttachment(a.id)}
                    style={{ display: "flex", alignItems: "center", border: "none", background: "transparent", color: "#B91C1C", cursor: "pointer", padding: 0 }}>
                    <X size={13} weight="bold" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ margin: 0, color: "#9CA3AF", fontSize: 11 }} data-testid="dp-attachments-empty">No extra files attached. Use "Attach file(s)" to add PO copies or supporting documents.</p>
          )}
        </div>

        <div id="dp-summary-sheet" style={{ padding: "18px 22px", fontFamily: "Arial, sans-serif", color: "#1f2937" }}>
          <div style={{ display: "flex", justifyContent: "space-between", borderBottom: "1px solid #E5E7EB", paddingBottom: 8, marginBottom: 14 }}>
            <strong style={{ fontSize: 14 }}>Internal: {data.customer_name}</strong>
            <strong style={{ fontSize: 14 }}>Invoice details: {inv.invoice_date || "—"}</strong>
          </div>

          <p style={{ fontSize: 13, margin: "0 0 10px" }}>Dear Sir,</p>
          {editText ? (
            <textarea data-testid="dp-email-intro" value={intro} onChange={(e) => setIntro(e.target.value)} rows={2}
              style={{ width: "100%", fontSize: 13, fontFamily: "Arial, sans-serif", border: "1px solid #0F766E", borderRadius: 6, padding: "8px 10px", margin: "0 0 16px", resize: "vertical" }} />
          ) : (
            <p style={{ fontSize: 13, margin: "0 0 16px", whiteSpace: "pre-line" }}>{intro}</p>
          )}

          {/* meta block */}
          <div style={{ border: "1px solid #E5C9A8", borderRadius: 4, overflow: "hidden" }}>
            <div style={{ display: "flex", gap: 24, background: "#DCE9F7", padding: "7px 10px", fontSize: 12, fontWeight: 700 }}>
              <span>Dispatch No : {data.dispatch_no || "—"}</span>
              <span>Shipment: {data.ship_type || "—"}</span>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 18, alignItems: "center", background: "#FBEEDD", padding: "7px 10px", fontSize: 12 }}>
              <label style={{ display: "flex", gap: 6, alignItems: "center", fontWeight: 700 }}>Invoice Date:
                <input type="date" data-testid="dp-summary-inv-date" value={inv.invoice_date} onChange={(e) => setInv((s) => ({ ...s, invoice_date: e.target.value }))} style={{ border: "1px solid #d1b48a", borderRadius: 4, padding: "2px 5px", fontSize: 12 }} /></label>
              <span style={{ fontWeight: 700 }}>{consignee}</span>
            </div>
            {data.transfer_party && <div data-testid="dp-summary-tp" style={{ background: "#CCFBF1", padding: "7px 10px", fontSize: 12, fontWeight: 700, color: "#0F766E" }}>Transfer Party: {data.tp_name || data.tp_code}</div>}

            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1080 }}>
                <thead><tr style={{ background: "#FBEEDD" }}>
                  {["S.No", "Po No.", "Part No.", "Rev", "Description", "PO Date", "Order Qty.", "Disp. Qty.", "Unit Price", `Value ${curLabel(c)}`, "Due Date", "Part Wt.", `Qty./${noun}`, `${noun} Qty`, `${noun} Size`, `${noun} Net.Wt`, `${noun} Gr.Wt`, "Total Net Wt", "Total Gr.Wt (Kg)"].map((h, i) => (
                    <th key={i} style={{ ...th, textAlign: i >= 6 && i !== 14 && i !== 4 && i !== 5 && i !== 10 ? "right" : "left" }}>{h}</th>))}
                </tr></thead>
                <tbody>
                  {data.lines.map((l) => (
                    <tr key={l.sno} data-testid={`dp-summary-row-${l.sno}`}>
                      <td style={td}>{l.sno}</td>
                      <td style={{ ...td, fontFamily: "var(--jde-font-mono)" }}>{l.po}</td>
                      <td style={{ ...td, fontFamily: "var(--jde-font-mono)" }}>{l.part_no}</td>
                      <td style={td}>{l.rev}</td>
                      <td style={td}>{l.description}</td>
                      <td style={td}>{fmtDate(l.po_date)}</td>
                      <td style={tdR}>{fmt(l.order_qty)}</td>
                      <td style={tdR}>{fmt(l.disp_qty)}</td>
                      <td style={tdR}>{Number(l.unit_price).toFixed(5)}</td>
                      <td style={tdR}>{money(l.value).replace("$", "")}</td>
                      <td style={td}>{fmtDate(l.due_date)}</td>
                      <td style={tdR}>{Number(l.part_wt).toFixed(5)}</td>
                      <td style={tdR}>{fmt(l.qty_per_pallet)}</td>
                      <td style={tdR}>{fmt(l.pallet_qty)}</td>
                      <td style={td}>{l.pallet_size}</td>
                      <td style={tdR}>{wt(l.pallet_net_wt)}</td>
                      <td style={tdR}>{wt(l.pallet_gross_wt)}</td>
                      <td style={tdR}>{wt(l.total_net_wt)}</td>
                      <td style={tdR}>{wt(l.total_gross_wt)}</td>
                    </tr>))}
                  <tr style={{ background: "#DCE9F7", fontWeight: 700 }}>
                    <td style={td} colSpan={9}>Total</td>
                    <td style={tdR}>{money(data.totals.value).replace("$", "")}</td>
                    <td style={td} colSpan={3}></td>
                    <td style={tdR}>{fmt(data.totals.pallet_qty)}</td>
                    <td style={td} colSpan={3}></td>
                    <td style={tdR}>{wt(data.totals.net_wt)}</td>
                    <td style={tdR}>{wt(data.totals.gross_wt)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div style={{ marginTop: 14, fontSize: 13 }}>
            <div>No. of {noun}s: <strong>{data.no_of_pallets}</strong></div>
            <div>Shipment Value: <strong>{curLabel(c)} {money(data.totals.value).replace("$", "")}</strong></div>
          </div>

          <div style={{ marginTop: 16, fontSize: 12.5 }}>
            <strong>Note:</strong>
            {editText ? (
              <textarea data-testid="dp-email-notes" value={notes.join("\n")} onChange={(e) => setNotes(e.target.value.split("\n"))} rows={Math.max(3, notes.length)}
                style={{ width: "100%", marginTop: 6, fontSize: 12.5, fontFamily: "Arial, sans-serif", border: "1px solid #0F766E", borderRadius: 6, padding: "8px 10px", resize: "vertical" }} />
            ) : (
              <ul style={{ margin: "6px 0 0", paddingLeft: 20 }}>
                {notes.filter((n) => n.trim()).map((n, i) => <li key={i} style={{ marginBottom: 3 }}>{n}</li>)}
              </ul>
            )}
            {editText && <div className="muted" style={{ fontSize: 10.5, marginTop: 4 }}>One note per line. The line-item table above is not editable.</div>}
          </div>
        </div>
      </div>

      {confirmMissing && (
        <div data-testid="dp-missing-confirm" onClick={(e) => e.stopPropagation()}
          style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.55)", zIndex: 70, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ background: "white", borderRadius: 10, width: "min(460px,100%)", boxShadow: "0 20px 60px rgba(0,0,0,.4)", overflow: "hidden" }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center", padding: "12px 16px", background: "#FFFBEB", borderBottom: "1px solid #FDE68A" }}>
              <Warning size={18} weight="fill" color="#D97706" />
              <span style={{ fontWeight: 700, color: "#92400E", fontSize: 13.5 }}>Missing PO copies</span>
            </div>
            <div style={{ padding: "14px 16px", fontSize: 13, color: "#334155" }}>
              <p style={{ margin: "0 0 8px" }}>These POs on this dispatch have <b>no copy on file</b>, so no link will be included for them:</p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, margin: "0 0 10px" }}>
                {confirmMissing.map((p) => (
                  <span key={p} style={{ fontFamily: "var(--jde-font-mono)", fontWeight: 700, fontSize: 12, padding: "2px 8px", borderRadius: 5, background: "#FEF3C7", color: "#92400E" }}>{p}</span>
                ))}
              </div>
              <p style={{ margin: 0, color: "#64748b", fontSize: 12 }}>Use the "Attach copy" button to add them first, or send anyway.</p>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, padding: "12px 16px", borderTop: "1px solid var(--jde-border)" }}>
              <button data-testid="dp-missing-cancel" onClick={() => setConfirmMissing(null)}
                style={{ padding: "8px 14px", border: "1px solid var(--jde-border)", borderRadius: 6, background: "white", fontSize: 12.5, fontWeight: 700, color: "#475569", cursor: "pointer" }}>Go back &amp; attach</button>
              <button data-testid="dp-missing-send-anyway" onClick={() => send(true)} disabled={sending}
                style={{ padding: "8px 16px", border: "none", borderRadius: 6, background: sending ? "#94A3B8" : "#D97706", color: "white", fontSize: 12.5, fontWeight: 700, cursor: sending ? "default" : "pointer" }}>{sending ? "Sending…" : "Send anyway"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ── Fill missing item-master packing/weights before an item can be dispatched ──
function ItemMasterModal({ item, onSave, onClose }) {
  const [f, setF] = useState({
    pack_type: "carton",
    units_per_carton: "", units_per_pallet: "",
    net_wt: "", gross_wt: "",
    lsize: item.lsize || "", wsize: item.wsize || "", hsize: item.hsize || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const isPallet = f.pack_type === "pallet";

  const submit = async () => {
    if (isPallet && !(Number(f.units_per_pallet) > 0)) return toast.error("Enter units per pallet");
    if (!isPallet && !(Number(f.units_per_carton) > 0)) return toast.error("Enter units per carton");
    if (!(Number(f.net_wt) > 0) || !(Number(f.gross_wt) > 0)) return toast.error("Enter net & gross weight");
    if (Number(f.gross_wt) < Number(f.net_wt)) return toast.error("Gross weight must be >= net weight");
    setSaving(true);
    try {
      await onSave({
        pack_type: f.pack_type,
        units_per_carton: Number(f.units_per_carton) || 0,
        units_per_pallet: Number(f.units_per_pallet) || 0,
        net_wt: Number(f.net_wt), gross_wt: Number(f.gross_wt),
        lsize: Number(f.lsize) || 0, wsize: Number(f.wsize) || 0, hsize: Number(f.hsize) || 0,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update master");
    } finally { setSaving(false); }
  };

  const lbl = { fontSize: 10, fontWeight: 700, color: "#6B7280", marginBottom: 3, display: "block" };
  const inp = { width: "100%", border: "1px solid var(--jde-border)", borderRadius: 6, padding: "7px 9px", fontSize: 13 };
  const wUnit = isPallet ? "PALLET" : "CARTON";

  return (
    <div data-testid="dp-master-modal" onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.55)", zIndex: 70, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "white", borderRadius: 10, width: "min(560px,100%)", boxShadow: "0 20px 60px rgba(0,0,0,.35)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "13px 16px", borderBottom: "1px solid var(--jde-border)" }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", fontWeight: 700, color: "#92400E" }}><Package size={17} weight="bold" /> Add packing details to master</div>
          <button data-testid="dp-master-close" onClick={onClose} className="omh-icon-btn" style={{ border: "1px solid var(--jde-border)", borderRadius: 6 }}><X size={15} weight="bold" /></button>
        </div>
        <div style={{ padding: "16px 18px" }}>
          <div style={{ fontSize: 12.5, marginBottom: 4 }}><strong>{item.icode}</strong> - {item.iname}</div>
          <div className="muted" style={{ fontSize: 11.5, marginBottom: 14 }}>This item's MS SQL master is missing packing/weights. Fill these to add it - they are saved to the live item master.</div>

          <div style={{ marginBottom: 12 }}>
            <span style={lbl}>PACK TYPE</span>
            <div style={{ display: "flex", gap: 8 }}>
              {["carton", "pallet"].map((t) => (
                <button key={t} data-testid={`dp-master-packtype-${t}`} onClick={() => set("pack_type", t)} style={{ flex: 1, padding: "8px", borderRadius: 6, border: `1px solid ${f.pack_type === t ? "#0F766E" : "var(--jde-border)"}`, background: f.pack_type === t ? "#0F766E" : "white", color: f.pack_type === t ? "white" : "#374151", fontWeight: 700, fontSize: 12, cursor: "pointer", textTransform: "capitalize" }}>{t}</button>))}
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
            {isPallet
              ? <div><span style={lbl}>UNITS PER PALLET *</span><input data-testid="dp-master-upp" type="number" value={f.units_per_pallet} onChange={(e) => set("units_per_pallet", e.target.value)} style={inp} /></div>
              : <div><span style={lbl}>UNITS PER CARTON *</span><input data-testid="dp-master-upc" type="number" value={f.units_per_carton} onChange={(e) => set("units_per_carton", e.target.value)} style={inp} /></div>}
            {isPallet && <div><span style={lbl}>UNITS PER CARTON (optional)</span><input data-testid="dp-master-upc" type="number" value={f.units_per_carton} onChange={(e) => set("units_per_carton", e.target.value)} style={inp} /></div>}
            <div><span style={lbl}>NET WEIGHT / {wUnit} *</span><input data-testid="dp-master-net" type="number" step="0.001" value={f.net_wt} onChange={(e) => set("net_wt", e.target.value)} style={inp} /></div>
            <div><span style={lbl}>GROSS WEIGHT / {wUnit} *</span><input data-testid="dp-master-gross" type="number" step="0.001" value={f.gross_wt} onChange={(e) => set("gross_wt", e.target.value)} style={inp} /></div>
          </div>

          <span style={lbl}>DIMENSIONS (L x W x H)</span>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 18 }}>
            <input data-testid="dp-master-l" type="number" step="0.001" placeholder="L" value={f.lsize} onChange={(e) => set("lsize", e.target.value)} style={inp} />
            <input data-testid="dp-master-w" type="number" step="0.001" placeholder="W" value={f.wsize} onChange={(e) => set("wsize", e.target.value)} style={inp} />
            <input data-testid="dp-master-h" type="number" step="0.001" placeholder="H" value={f.hsize} onChange={(e) => set("hsize", e.target.value)} style={inp} />
          </div>

          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button data-testid="dp-master-cancel" onClick={onClose} style={{ padding: "8px 16px", borderRadius: 6, border: "1px solid var(--jde-border)", background: "white", fontWeight: 700, fontSize: 12, cursor: "pointer" }}>Cancel</button>
            <button data-testid="dp-master-save" onClick={submit} disabled={saving} style={{ padding: "8px 18px", borderRadius: 6, border: "none", background: "#0F766E", color: "white", fontWeight: 700, fontSize: 12, cursor: "pointer" }}>{saving ? "Saving..." : "Save & add"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Manual "Route via transfer party" picker — lets an operator route a customer that has NO
// TP_CODE in the ERP master (e.g. MPY / MacLean Power-York) via an existing transfer party (AVM).
function ManualTpPicker({ tpOptions, onPick }) {
  return (
    <>
      <span className="muted" style={{ fontSize: 11, fontWeight: 700 }} title="Bill/route this dispatch via a transfer party (e.g. AVM) even though this customer has no default in the ERP master">Route via transfer party?</span>
      <select data-testid="dp-tp-manual-select" className="jde-select" style={{ minWidth: 150, fontSize: 11 }}
        value="" onChange={(e) => { const o = tpOptions.find((t) => t.tp_code === e.target.value); if (o) onPick(o); }}>
        <option value="">Select party…</option>
        {tpOptions.map((t) => <option key={t.tp_code} value={t.tp_code}>{t.tp_name || t.tp_code}</option>)}
      </select>
    </>
  );
}
